import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { CustomPopover, GenericError, If, NoPermissions, SidePanel } from 'components';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { ArrowRightIcon, CaretDownIcon, InvoicesTabIcon, JobsTabIcon, NavCloseIcon, WhiteInfoIcon } from 'assets/icons';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectIsActionsBlocked,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { VoidFn, clevertapEvents } from 'types/baseTypes';
import CreateCustomer from 'containers/create-customer/CreateCustomer';
import { CreateCustomerModalTypes } from 'constants/CustomerConstants';
import { SidePanelTypes } from 'constants/sidePanelConstants';
import { logAnalyticEvent } from 'utils/analytics';
import { NotifierTypes } from 'constants/notifierConstants';
import { InfoMesssages } from 'types/infoMessages';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import CustomerInvoiceWidget from './CustomerInvoiceWidget';
import CustomerJobsWidget from './CustomerJobsWidget';
import CustomerContactInformationWidget from './CustomerContactInformationWidget';
import CustomerCOFWidget from '../../customers/components/CustomerCOFWidget';
import CustomerMoreDetailsWidget from './CustomerMoreDetailsWidget';
import CustomerAdditionalInfoWidget from './CustomerAdditionalInfoWidget';
import { usePaginatedGetInvoicesQuery } from '../hooks/usePaginatedGetInvoicesQuery';
import { useLazyGetCustomerByIdQuery } from '../api';
import { CustomerDetails } from '../invoices.model';
import { useLazyGetCustomerCardsQuery, useUpdateCustomerMutation } from 'containers/customers/api';
import { usePaginatedGetJobsQuery } from 'containers/jobs/hooks/usePaginatedGetJobsQuery';
import { PopoverArrowTypes } from 'constants/common';
import MoreMenu from 'containers/receipts/components/MoreMenu';
import { resetInvoiceState } from '../InvoicesSlice';
import { CustomerPanelRouteState } from 'routes/types';
import { resetCreateJobState } from 'containers/jobs/create-edit-job/createJobSlice';
import { useLazyGetCustomerEstimatesQuery } from 'containers/customers/api';
import CustomerEstimatesWidget from 'containers/estimates/components/CustomerEstimatesWidget';
import MerchantOrTeamBlockedInfoPanel from 'components/merchant-or-team-blocked/MerchantOrTeamBlockedInfoPanel';
import { getCustomerFullName } from 'containers/customers/Utils';

interface CustomerPanelProps {
  customerData?: CustomerDetails;
  onClose?: VoidFn;
  onInvoiceClick?: (invoiceId: string) => void;
  openEditModal?: () => void;
  routeToNextPage?: ({ type, id }: { type: SidePanelTypes; id: string }) => void;
}

const CustomerPanel: FC<CustomerPanelProps> = () => {
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const userDetails = useAppSelector(selectUserDetails);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [getCustomerByIdQuery, getCustomerByIdState] = useLazyGetCustomerByIdQuery();
  const [getCustomerEstimates, { data: customerEstimates, isLoading: loadingEstimates }] =
    useLazyGetCustomerEstimatesQuery();
  const [getCustomerCards, customerCardsDataResponse] = useLazyGetCustomerCardsQuery();
  const [isFromJob, setIsFromJob] = useState(false);
  const { data: customerData, isSuccess: customerDataSuccess, isFetching: fetchingCustomerData } = getCustomerByIdState;
  const {
    list: invoicesList,
    isLoading: isLoadingInvoices,
    setQueryParams: setInvoicesQueryParams,
    totalListCount: invoicesCount
  } = usePaginatedGetInvoicesQuery(3);
  const {
    list: jobsList,
    isLoading: isLoadingJobs,
    setQueryParams: setJobQueryParams,
    totalListCount: jobsCount
  } = usePaginatedGetJobsQuery(3);
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getNotifications);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);

  const { customerId, rootCustomerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSidePanelClose, resetCustomersQuery } = useOutletContext<any>();

  const [openCreateCustomerModal, setOpenCreateCustomerModal] = useState(false);
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [showCreateNewOpions, setShowCreateNewOpions] = useState(false);

  const [updateCustomer, { isSuccess: updateCustomerSuccess }] = useUpdateCustomerMutation();

  const isSelfCreatedCustomer = customerData?.merchantId === userDetails?.id;

  const refreshCustomerDetails = () => {
    const currentCustomerId = customerId ? customerId : rootCustomerId;
    getCustomerByIdQuery({ customerId: currentCustomerId, teamId: currentTeam?.id });
    getCustomerEstimates({ teamId: currentTeam?.id, customerId: currentCustomerId });
    if (currentTeam?.id && currentCustomerId) {
      getCustomerCards({
        teamId: currentTeam.id,
        customerId: currentCustomerId
      });
    }
  };

  useEffect(() => {
    if (updateCustomerSuccess) {
      refreshCustomerDetails();
      if (resetCustomersQuery) {
        resetCustomersQuery();
      }
    }
  }, [updateCustomerSuccess]);

  useEffect(() => {
    if (customerId || rootCustomerId) {
      const currentCustomerId = customerId ? customerId : rootCustomerId;
      if (currentTeam?.id) {
        getCustomerCards({
          teamId: currentTeam.id,
          customerId: currentCustomerId
        });
      }
      getCustomerByIdQuery({ customerId: currentCustomerId, teamId: currentTeam?.id });
      setInvoicesQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        customerId: [currentCustomerId]
      }));
      setJobQueryParams(prevQueryParams => ({ ...prevQueryParams, customerIds: [currentCustomerId] }));
      logAnalyticEvent(clevertapEvents.webCustomersListDetails);
      getCustomerEstimates({ teamId: currentTeam?.id, customerId: currentCustomerId }); // Trigger lazy query
    }
  }, [customerId, currentTeam?.id, rootCustomerId]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState = location.state as CustomerPanelRouteState;
    if (routeState?.fromSidePanel) {
      setShowBackArrow(true);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    if (routeState?.isFromJob) setIsFromJob(routeState.isFromJob);

    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  useEffect(() => {
    if (!customerPermissions.viewCustomersCreatedByTeam) {
      setHasPermissionsToView(false);
    }
  }, [customerPermissions.viewCustomersCreatedByTeam]);

  const closeCreateCustomerModal = () => {
    setOpenCreateCustomerModal(false);
  };

  const isEditCustomerAllowed = () => {
    if (customerPermissions.editCustomersCreatedByTeam) {
      return true;
    }
    if (customerPermissions.editExistingCustomers && isSelfCreatedCustomer) {
      return true;
    }
    return false;
  };

  const openEditCustomerModal = () => {
    setOpenCreateCustomerModal(true);
  };

  const onInfoClick = (id, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const onSidePanelClose = () => {
    if (showBackArrow) {
      navigate(-1);
    } else {
      handleSidePanelClose();
    }
  };

  const getCustomerName = () => {
    if (customerData) {
      if (customerData.name || customerData.lastName) {
        return `${customerData.name} ${customerData.lastName}`;
      } else if (customerData.phoneNumber) {
        return customerData.phoneNumber;
      }
    }
    return '-';
  };

  const handleEditCustomer = customerPayload => {
    updateCustomer(customerPayload);
    setOpenCreateCustomerModal(false);
  };

  const bottomBar = [
    {
      id: 'customer-sidepanel-bottom-edit-button',
      onClickHandler: () => {
        logAnalyticEvent(clevertapEvents.webCustomersListDetailsEdit, { 'Customer Id': customerData?.id });
        openEditCustomerModal();
      },
      btnText: 'Edit',
      disabledReason: InfoMesssages.editCustomerNoPermissionMessage,
      isDisabled: !isEditCustomerAllowed()
    }
  ];

  const renderCustomerDetailsPanel =
    customerDataSuccess && !fetchingCustomerData && !isLoadingInvoices && !isLoadingJobs;

  const toggleIsMoreOpen = () => {
    setShowCreateNewOpions(current => !current);
  };

  const createNewAnchorElem = () => (
    <div
      id="customer-panel-bottom-bar-more-button"
      className="flex shrink grow cursor-pointer items-center justify-center gap-2
      bg-secondary py-3.5 font-semibold text-primary hover:opacity-95">
      <div>Create New</div>
      <div className="path-stroke-current path">
        <CaretDownIcon className="rotate-180" />
      </div>
    </div>
  );

  const createNewMenuItems = [
    {
      id: 'create-new-button-create-invoice',
      icon: <InvoicesTabIcon />,
      text: 'Invoice',
      onClick: () => {
        dispatch(resetInvoiceState());
        navigate('/invoices/create?customerId=' + customerData?.id);
      }
    },
    {
      id: 'create-new-button-create-job',
      icon: <JobsTabIcon className="w-5 shrink-0" />,
      text: 'Job',
      onClick: () => {
        dispatch(resetCreateJobState());
        navigate('/jobs/create?customerId=' + customerData?.id);
      }
    }
  ];

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      {isActionsBlocked ? (
        <MerchantOrTeamBlockedInfoPanel onBack={onSidePanelClose} />
      ) : (
        <div className="flex h-full w-96 flex-col">
          {fetchingCustomerData || isLoadingInvoices || isLoadingJobs || loadingEstimates ? (
            <div className="flex h-full w-96 items-center justify-center">
              <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
            </div>
          ) : (
            renderCustomerDetailsPanel && (
              <>
                <div className="flex w-full items-center gap-2 p-5 pl-3">
                  <div className="cursor-pointer" onClick={onSidePanelClose}>
                    {showBackArrow ? (
                      <ArrowRightIcon className="rotate-180" id="customer-panel-back-arrow" />
                    ) : (
                      <NavCloseIcon id="customer-panel-close" />
                    )}
                  </div>
                  <div id="customer-panel-head" className="text-17px font-semibold text-headingGray">
                    {getCustomerName()}
                  </div>
                </div>
                {hasPermissionsToView ? (
                  <>
                    <div className="customNormalScroll flex grow flex-col gap-5 overflow-y-auto">
                      <CustomerContactInformationWidget
                        customerData={customerDataSuccess ? customerData : null}
                        isEditCustomerAllowed={isEditCustomerAllowed()}
                        openEditCustomerModal={openEditCustomerModal}
                        onInfoClick={onInfoClick}
                        refreshCustomerDetails={refreshCustomerDetails}
                        isEditable={!isFromJob}
                      />
                      {customerPermissions.viewCustomerCards && (
                        <CustomerCOFWidget
                          customerCardsDataResponse={customerCardsDataResponse}
                          isEditable={customerPermissions.editCustomerCards}
                          canCreateCards={customerPermissions.createCustomerCards}
                          customerCardObject={{
                            name: getCustomerFullName(customerData) || '',
                            email: customerData?.email || '',
                            customerId: customerData?.id || '',
                            billingAddress: customerData?.billingAddress || undefined
                          }}
                        />
                      )}
                      <CustomerJobsWidget
                        customerJobsList={jobsList}
                        totalCustomerJobsCount={jobsCount}
                        customerId={customerData?.id}
                      />
                      <CustomerInvoiceWidget
                        customerData={customerDataSuccess ? customerData : null}
                        customerInvoicesList={invoicesList}
                        totalCustomerInvoicesCount={invoicesCount}
                      />
                      <CustomerEstimatesWidget // Add the CustomerEstimatesWidget here
                        customerEstimatesList={customerEstimates.data} // Pass the customer estimates data
                        totalCustomerEstimatesCount={customerEstimates.data?.length} // Pass the total count
                        customerId={customerData?.id} // Pass the customer ID
                      />
                      <CustomerMoreDetailsWidget
                        customerData={customerDataSuccess ? customerData : null}
                        openEditCustomerModal={openEditCustomerModal}
                        isEditCustomerAllowed={isEditCustomerAllowed() && !isFromJob}
                      />
                      <CustomerAdditionalInfoWidget customerData={customerDataSuccess ? customerData : null} />
                    </div>
                    <div className="mt-auto flex justify-between gap-px pt-2">
                      <div
                        className="flex w-1/2 shrink grow cursor-pointer items-center
                      justify-center gap-2 bg-secondary hover:opacity-95"
                        onClick={toggleIsMoreOpen}>
                        <CustomPopover
                          show={showCreateNewOpions}
                          anchorComponent={createNewAnchorElem}
                          onTogglePopover={toggleIsMoreOpen}
                          onClosePopover={() => setShowCreateNewOpions(false)}
                          offset="-right-3 bottom-14"
                          minWidth="min-w-[160px]"
                          showArrow={true}
                          arrowType={PopoverArrowTypes.BOTTOM}
                          highlightAnchor={false}
                          showOnTop={true}>
                          {<MoreMenu menuItems={createNewMenuItems} />}
                        </CustomPopover>
                      </div>
                      {bottomBar?.map((item, index) => (
                        <div
                          id={`customer-bottom-bar-${item.btnText}-button`}
                          key={item.btnText + index}
                          onClick={() => {
                            if (item.isDisabled) {
                              onInfoClick(item.id, item.disabledReason);
                            } else {
                              item.onClickHandler();
                            }
                          }}
                          className={`flex w-1/2 shrink grow cursor-pointer items-center justify-center gap-2
                  bg-secondary py-3.5 font-semibold text-primary hover:opacity-95
                  ${item.isDisabled ? 'bg-[#9BBADD] hover:opacity-100' : ''}
                `}>
                          <div className={`text-sbase ${item.isDisabled && 'opacity-50'}`}>{item.btnText}</div>
                          {item.isDisabled && <WhiteInfoIcon />}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <NoPermissions />
                )}
                <If condition={Boolean(openCreateCustomerModal)}>
                  <CreateCustomer
                    open={openCreateCustomerModal}
                    onClose={closeCreateCustomerModal}
                    panelType={CreateCustomerModalTypes.EDIT_CUSTOMER}
                    customerData={{
                      ID: customerData?.id,
                      name: customerData?.name,
                      lastName: customerData?.lastName,
                      email: customerData?.email,
                      phoneNumber: customerData?.phoneNumber,
                      companyName: customerData?.companyName,
                      referenceNumber: customerData?.referenceNumber,
                      note: customerData?.note
                    }}
                    customerDataFetchSuccess={true}
                    customerDataFetchLoading={false}
                    onAddCustomer={handleEditCustomer}
                  />
                </If>
              </>
            )
          )}
          {!fetchingCustomerData &&
            !isLoadingInvoices &&
            !isLoadingJobs &&
            !customerDataSuccess &&
            hasPermissionsToView && <GenericError handleErrorBack={onSidePanelClose} />}
        </div>
      )}
    </SidePanel>
  );
};

export default CustomerPanel;
